/**
 * Aegix One Personal Safety App — Entry Point
 *
 * Integrates auth, React Query, and the shared Aegix theme.
 *
 * **ADR-118 #18**: auth is `@aegix/auth-identity`'s
 * `<AuthProvider>` + `createAllauthAdapter()`. ONE composes only the
 * Layer-1 (auth-core) + Layer-2 (auth-identity) libs — no permissions
 * (Layer 3) or organization (Layer 4) — matching its parent-facing
 * scope (no orgs, no perms).
 *
 * `cookieName` is `'sessionid'` (Django's allauth session cookie).
 * The lib's boot-time reconcile uses this to detect
 * Branch-1 (happy path: cookie + localStorage agree) vs. Branch-3
 * (token without cookie → expired). Branch-2 (cookie without
 * localStorage) is rare on this app since allauth issues the JWT in
 * the same response that sets the cookie; if it happens the adapter's
 * `fetchMe` probe runs against `/_allauth/browser/v1/auth/session`.
 *
 * ONE follows the unified-auth architecture documented in the
 * ADR-118 amendment:
 *
 *   - `storage={oneStorage}` — the `BridgedStorageConfig`-based
 *     adapter AIM and SMS use. Canonical
 *     `aegix.auth.v1.*` keys, `legacyKeys` promotion of the
 *     pre-canonical `aegix_one_*` slots on first read (no-squash
 *     existing sessions), mobile-pattern split (access in
 *     `sessionStorage`, refresh + user in `localStorage`).
 *
 *   - `refreshAdapter={oneRefreshAdapter}` — the boot-time refresh
 *     adapter that lets the mobile pattern survive a fresh load:
 *     when `sessionStorage` is empty but the refresh token persists
 *     in `localStorage`, the provider mints a new access via
 *     `/api/v2/api-token-refresh/` before deciding logged-out.
 *     Wired into the boot reconcile path.
 *
 * Both `oneStorage` and `oneRefreshAdapter` live in
 * `lib/one-auth.ts` — the ONE-side auth seam where the lint rule
 * `aegix/no-raw-token-storage-access` whitelists raw token access.
 */

import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, type AllauthUser } from '@aegix/auth-identity';
import { Provider as UIProvider } from '@/components/ui/provider';
import { SocketProvider } from '@/contexts/SocketContext';
import { SocketMessageRouter } from '@/contexts/SocketMessageRouter';
import App from './App';
import { redirectToLoginForSignOut } from './lib/logoutRedirect';
import {
  oneStorage,
  oneRefreshAdapter,
  oneLoginAdapter,
} from './lib/one-auth';
import { registerPushServiceWorker } from './push/serviceWorker';
import './i18n/config';

// Install the Firebase messaging service worker at bootstrap so it
// is registered and controlling before the post-login FCM token registration
// (PushNotificationsManager) needs it. Auth-gated work stays in the manager.
registerPushServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

// The single shared adapter instance lives in
// `lib/one-auth.ts` (the canonical auth seam) with `deviceIdFetch` wired
// as its `fetchImpl`, so its JWT mints carry `x-device-id` and the real
// instance is testable without rendering the app.

const LOCALE_STORAGE_KEYS = ['i18nextLng'];

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <UIProvider>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <AuthProvider<AllauthUser>
              loginAdapter={oneLoginAdapter}
              refreshAdapter={oneRefreshAdapter}
              storage={oneStorage}
              cookieName="sessionid"
              localeStorageKeys={LOCALE_STORAGE_KEYS}
              // `onLogout` only fires from the deliberate
              // `logout()` action (ProfileDrawer sign-out) — never on
              // session expiry — so it clears any stale return
              // target before bouncing to /login. The involuntary
              // (expiry) redirect preserves the location instead; see
              // lib/logoutRedirect.ts.
              onLogout={redirectToLoginForSignOut}
            >
              <SocketProvider>
                <SocketMessageRouter>
                  <React.Suspense fallback={null}>
                    <App />
                  </React.Suspense>
                </SocketMessageRouter>
              </SocketProvider>
            </AuthProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </UIProvider>
    </React.StrictMode>
  );
}
